import { useCurrentApp, useUserDataStore } from '@/stores/userDataStore';
import { StandaloneMediaAsset, MediaAssetType } from '@/types/media';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

type MediaAssetsLoaderProps = {
  queryKey: string[];
  loaderFn: (props: { appKey: string }) => Promise<{ mediaAssets: StandaloneMediaAsset[] }>;
};

const useMediaAssetsLoader = ({ queryKey, loaderFn }: MediaAssetsLoaderProps) => {
  const { appKey } = useCurrentApp();
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => loaderFn({ appKey }),
    staleTime: 5 * 60 * 1000,
  });

  return { mediaAssets: data?.mediaAssets, isLoading };
};

export const useMediaAssetsPrefetch = ({ loaderFn, queryKey }: MediaAssetsLoaderProps) => {
  const { appKey, isLoggedIn } = useCurrentApp();
  const queryClient = useQueryClient();
  const prefetchMediaAssets = useCallback(() => {
    if (appKey && isLoggedIn) {
      queryClient.prefetchQuery({
        queryKey: [...queryKey, appKey],
        queryFn: () => loaderFn({ appKey }),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [appKey, isLoggedIn, queryClient, loaderFn, queryKey]);

  useEffect(() => {
    prefetchMediaAssets();
  }, [prefetchMediaAssets]);

  return prefetchMediaAssets;
};

export const useMediaAssetsInvalidator = (mediaAssetType?: MediaAssetType) => {
  const queryClient = useQueryClient();
  const invalidateMediaAssets = useCallback(() => {
    if (!mediaAssetType) {
      queryClient.invalidateQueries({ queryKey: ['image-assets'] });
      queryClient.invalidateQueries({ queryKey: ['video-assets'] });
    } else if (mediaAssetType === MediaAssetType.image) {
      queryClient.invalidateQueries({ queryKey: ['image-assets'] });
    } else if (mediaAssetType === MediaAssetType.video) {
      queryClient.invalidateQueries({ queryKey: ['video-assets'] });
    }
  }, [queryClient, mediaAssetType]);
  return invalidateMediaAssets;
};

export default useMediaAssetsLoader;
